// Webpack 4 needs the browser entry explicitly; adapter 26 validates the
// nonce in the ID token, matching the current identity provider.
import Keycloak from 'keycloak-js/lib/keycloak.js';
import axios from 'axios';
import Auth from '@groovepages/gd-lib/auth';
import { logoutFromGroove } from '../../../shared/session-logout.mjs';
import Cookies from 'js-cookie';
import store from '@/store';
import { getPortalUrl } from './portal-auth';
import { connectSignInWindow } from './portal-embed-bridge';
import { getEmbeddedUser, getEmbeddedSessionVersion, readEmbeddedSession, setEmbeddedUser, clearEmbeddedUser } from './portal-embed-session';

class KeycloakService {
  constructor() {
    this.keycloak = null;
    this.initialization = null;
  }

  async init() {
    // Cross-site frames must never navigate to the identity provider. The
    // widget offers a user-initiated link to this same portal in a new tab.
    if (window.self !== window.top) return this.restoreEmbeddedSession();
    if (!this.initialization) this.initialization = this.initialize();
    return this.initialization;
  }

  async restoreEmbeddedSession() {
    if (Cookies.get('db71ab6b0872a8afd28afe1adf7de4b5') === '4236a440a662cc8253d7536e5aa17942') {
      clearEmbeddedUser();
      return false;
    }
    if (getEmbeddedUser()) return true;
    const sessionVersion = getEmbeddedSessionVersion();
    let sharedUser;
    try { sharedUser = Auth.authenticatedUser(); } catch (_) { /* Restricted storage. */ }
    const remembered = readEmbeddedSession();
    const candidates = [sharedUser, remembered && (!sharedUser || String(sharedUser.id) === String(remembered.id)) ? remembered : null]
      .filter(user => user && user.id && user.token);
    for (const candidate of candidates) {
      try {
        let user;
        if (candidate.auth_keycloak) user = await this.validateEmbeddedToken(candidate.token);
        else {
          // Legacy accounts also need a fresh authenticated API response. Use a
          // dedicated client so cached global interceptors cannot replace it.
          const { data } = await axios.create().get(`${window.config.apiCoreUrl}/user-status`, {
            headers: { Authorization: `Bearer ${candidate.token}`, 'x-auth-provider': 'auth0' }, timeout: 10000
          });
          if (!data || ![0, 3].includes(data.blocked)) throw new Error('Session unavailable');
          user = candidate;
        }
        // A logout or newer explicit sign-in must win over an in-flight check.
        if (sessionVersion !== getEmbeddedSessionVersion()) return Boolean(getEmbeddedUser());
        setEmbeddedUser(user);
        store.commit('login', user);
        return true;
      } catch (_) {
        if (sessionVersion !== getEmbeddedSessionVersion()) return Boolean(getEmbeddedUser());
        // Try another saved token, then show the sign-in action.
      }
    }
    clearEmbeddedUser();
    return false;
  }

  async initialize() {
    this.keycloak = new Keycloak({
      url: window.gdEnv?.VUE_APP_KEYCLOAK_URL || 'https://auth.groovetech.io/auth',
      realm: window.gdEnv?.VUE_APP_KEYCLOAK_REALM || 'gd-apis-live',
      clientId: window.gdEnv?.VUE_APP_KEYCLOAK_CLIENT_ID || 'app-groovecm'
    });
    try {
      const authenticated = await this.keycloak.init({
        // Consume an OAuth callback, but never launch a hidden SSO iframe.
        // Sign-in starts only from the explicit portal button or entry link.
        checkLoginIframe: false,
        responseMode: 'query',
        pkceMethod: 'S256',
        enableLogging: false
      });
      if (!authenticated) return false;
      // A dedicated client avoids the legacy interceptor replacing the fresh
      // Keycloak token with a stale stored token. Require the real API profile.
      const response = await axios.create().post(
        `${window.config.apiCoreUrl}/auth/keycloak-token-login`, {}, {
          headers: { Authorization: `Bearer ${this.keycloak.token}`, 'x-auth-provider': 'keycloak' }
        }
      );
      if (!response.data || !response.data.id) throw new Error('Profile unavailable');
      const user = { ...response.data, auth_keycloak: 1, token: this.keycloak.token };
      // gd-lib gives its legacy cookie precedence over localStorage, including
      // a logout sentinel. Retire it only after validating the new SSO profile.
      Cookies.remove('db71ab6b0872a8afd28afe1adf7de4b5', { path: '/' });
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('keycloak_token', this.keycloak.token);
      localStorage.setItem('keycloak_id_token', this.keycloak.idToken);
      store.commit('login', user);
      delete axios.defaults.headers.common['x-auth-provider'];
      this.keycloak.onTokenExpired = async () => {
        try {
          await this.keycloak.updateToken(30);
          user.token = this.keycloak.token;
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('keycloak_token', this.keycloak.token);
          localStorage.setItem('keycloak_id_token', this.keycloak.idToken);
          store.commit('login', user);
        } catch (_) {
          // An explicit sign-in returns to the exact portal again.
          window.location.reload();
        }
      };
      connectSignInWindow(window, this.keycloak.token);
      return true;
    } catch (_) {
      return false;
    }
  }

  // A paired popup supplies an access token, but never an identity to trust.
  // Validate against the backend before the iframe uses the resulting profile.
  async validateEmbeddedToken(token) {
    if (window.self === window.top) throw new Error('Embedded sign-in only');
    const { data } = await axios.create().post(
      `${window.config.apiCoreUrl}/auth/keycloak-token-login`, {}, {
        headers: { Authorization: `Bearer ${token}`, 'x-auth-provider': 'keycloak' },
        timeout: 15000
      }
    );
    if (!data || !data.id) throw new Error('Profile unavailable');
    return { ...data, auth_keycloak: 1, token };
  }

  logout(options = {}) {
    return logoutFromGroove({
      browser: window, auth: Auth, axios, keycloak: this.keycloak,
      clearAppSession: options.clearAppSession
    });
  }

  async login() {
    if (window.self !== window.top) return;
    await this.init();
    return this.keycloak.login({ redirectUri: getPortalUrl(window.location.href) });
  }
}

export default new KeycloakService();
